// リソース宣言が共用する小道具。
import { Timestamp } from 'firebase/firestore';
import { z } from 'zod';
import { fetchCustomers } from '@modules/customer/services/customer.service';
import { fetchEmployees } from '@modules/master/services/employee.service';
import { fetchBusinessTypes } from '@modules/master/services/businessType.service';
import { fetchUsergroups } from '@modules/master/services/usergroup.service';
import type { BusinessType, Customer, Employee, Usergroup } from '@shared/types';

/** LLM へ渡す最大件数。多すぎると小型モデルの文脈が溢れる。 */
export const MAX_ROWS = 20;

/** Firestore の Timestamp を YYYY-MM-DD に。値が無ければ null。 */
export function toDateString(ts: Timestamp | null | undefined): string | null {
  if (!ts || typeof ts.toDate !== 'function') return null;
  const d = ts.toDate();
  const month = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

/**
 * 番号の表記ゆれを吸収する。人は全角で打つし、ハイフンにも各種ある。
 * 例: 「０００１－０１」「0001ー01」→ 「0001-01」、「ｃ０００１」→「C0001」
 */
export function normalizeCode(raw: string): string {
  return raw
    .trim()
    .replace(/[－ー―‐−]/g, '-') // 各種ダッシュを半角ハイフンへ
    .replace(/[Ａ-Ｚａ-ｚ０-９]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0))
    .toUpperCase();
}

/** YYYY-MM-DD の文字列。LLM が日付を組みやすい形で受け、Timestamp へ変換する。 */
export const dateString = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, '日付は YYYY-MM-DD 形式')
  .refine((s) => {
    // 形だけでなく実在する日付か（2026-13-45 等を弾く。Invalid Date を Firebase に渡さない）
    const d = new Date(`${s}T00:00:00`);
    if (Number.isNaN(d.getTime())) return false;
    const pad = (n: number) => `${n}`.padStart(2, '0');
    return s === `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }, '実在する日付を YYYY-MM-DD で指定');

/** YYYY-MM-DD 文字列を Firestore の Timestamp へ（ローカルタイム 00:00）。 */
export function toTimestamp(ymd: string): Timestamp {
  return Timestamp.fromDate(new Date(`${ymd}T00:00:00`));
}

/**
 * 顧客名または顧客コードから取引先を解決する。
 * 【原則】Firestore の ID を LLM に組ませない（実機で顧客コードを ID として渡し、
 * 存在しない顧客に紐づく受注ができた事故への対策）。名前/コードを受けてツール側で解決する。
 * - コードの完全一致（表記ゆれ正規化）を最優先、次に名前・カナの部分一致
 * - 複数一致は候補を並べてエラー（勝手に決めない）、0件は list_records へ誘導
 */
export async function resolveCustomer(
  nameOrCode: string,
  partnerType?: 'customer' | 'supplier' | 'subcontractor'
): Promise<Customer> {
  const all = await fetchCustomers();
  const pool = partnerType ? all.filter((c) => (c.partnerTypes ?? []).includes(partnerType)) : all;
  const code = normalizeCode(nameOrCode);
  const byCode = pool.find((c) => normalizeCode(c.code) === code);
  if (byCode) return byCode;
  const byName = pool.filter((c) => c.name.includes(nameOrCode) || c.nameKana.includes(nameOrCode));
  if (byName.length === 1) return byName[0];
  if (byName.length > 1) {
    const candidates = byName.slice(0, 5).map((c) => `${c.code} ${c.name}`).join(' / ');
    throw new Error(`「${nameOrCode}」に一致する取引先が${byName.length}件あります: ${candidates}。顧客コードで指定してください`);
  }
  throw new Error(`「${nameOrCode}」に一致する取引先が見つかりません。list_records(customer) で確認してください`);
}

/**
 * マスタ一覧から名前で1件を解決する共通ロジック（resolveCustomer と同じ設計）。
 * - 名前の完全一致を最優先、次に名前・別名（フリガナ/略称等）の部分一致
 * - 複数一致は候補を並べてエラー（勝手に決めない）、0件もエラー
 */
function pickByName<T extends { id: string; name: string }>(
  pool: T[],
  name: string,
  label: string,
  aliases: (item: T) => (string | null | undefined)[] = () => []
): T {
  const exact = pool.filter((m) => m.name === name);
  if (exact.length === 1) return exact[0];
  const hits =
    exact.length > 1
      ? exact
      : pool.filter((m) => m.name.includes(name) || aliases(m).some((a) => a && a.includes(name)));
  if (hits.length === 1) return hits[0];
  if (hits.length > 1) {
    const candidates = hits.slice(0, 5).map((m) => m.name).join(' / ');
    throw new Error(`「${name}」に一致する${label}が${hits.length}件あります: ${candidates}。正確な名前で指定してください`);
  }
  throw new Error(`「${name}」に一致する${label}が見つかりません。マスタに登録されている名前で指定してください`);
}

/**
 * 担当者（社員マスタ）を名前から解決する。
 * 【原則】Firestore の ID を LLM に組ませない。assignedUserId / chiefEmployerId は
 * 社員マスタ（employees）の ID なので、名前を受けてツール側で解決する。
 */
export async function resolveEmployee(name: string): Promise<Employee> {
  const all = await fetchEmployees();
  return pickByName(all, name, '担当者', (e) => [e.furi]);
}

/** 業種（業種マスタ）を名前から解決する。businessTypeId に使う。 */
export async function resolveBusinessType(name: string): Promise<BusinessType> {
  const all = await fetchBusinessTypes();
  return pickByName(all, name, '業種', (b) => [b.label]);
}

/** 担当部署（部署マスタ usergroup）を名前から解決する。usergroupId に使う。 */
export async function resolveUsergroup(name: string): Promise<Usergroup> {
  const all = await fetchUsergroups();
  return pickByName(all, name, '部署', (u) => [u.shortName]);
}

/**
 * 印鑑フラグ（担当者印/確認者印/会社印）。AIに開放してよい boolean 3つ。
 * 【注意】部分指定を許すと本体スキーマの default が残りを false で埋めて既存の印が
 * 消えるため、3つとも必ず指定させる。
 */
export const aiStampFlagsSchema = z.object({
  tantou: z.boolean(),
  kakunin: z.boolean(),
  kaisha: z.boolean(),
});

/** AIが渡す明細1行（帳票共通）。 */
export interface AiDocItem {
  name: string;
  quantity: number;
  unit?: string;
  unitPrice: number;
}

/** AiDocItem の検証スキーマ（delivery/invoice/purchase_order 等の各リソースで共用）。 */
export const aiDocItemSchema = z.object({
  name: z.string().min(1, '品名は必須'),
  quantity: z.number().positive('数量は正の数'),
  unit: z.string().optional(),
  unitPrice: z.number().finite('単価は数値'),
});

/** 帳票共通の lineItems 形式（delivery/invoice/purchase_order等が本体ドキュメントに持つ配列）。 */
export function toDocLineItems(items: AiDocItem[]) {
  return items.map((item, index) => ({
    id: `ai-${index + 1}`,
    title: item.name,
    qty: item.quantity,
    unitId: '', // 単位マスタは解決しない（レビュー時に人が設定）
    unitPrice: item.unitPrice,
    amount: item.quantity * item.unitPrice,
    taxRate: 10 as const,
  }));
}

/** 帳票共通の金額計算（PurchaseOrderFormPage.calcTotals と同一ロジック・標準税率のみ）。 */
export function calcDocTotals(items: AiDocItem[], mode: 'floor' | 'ceil' | 'round' | 'manual') {
  const round = (v: number) => (mode === 'ceil' ? Math.ceil(v) : mode === 'round' ? Math.round(v) : Math.floor(v));
  let subtotal = 0;
  let taxAmount = 0;
  for (const item of items) {
    const s = item.quantity * item.unitPrice;
    subtotal += s;
    taxAmount += round(s * 0.1);
  }
  return { subtotal, taxAmount, total: subtotal + taxAmount };
}
